/**
 * ChatPage — /chat  (Help & Tutorials hub)
 *
 * Two tabs:
 *   Help Chat       — AI support chat (existing ChatWindow)
 *   Video Tutorials — Tutorial tree with per-series grouping
 *
 * When navigated with location.state = { tab: "tutorials", tutorialId: "06" }
 * (from PageHelpBubble) the tutorials tab is auto-selected and the relevant
 * tutorial is highlighted + scrolled into view.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import ChatWindow from "../../components/chat/ChatWindow";
import TutorialTree from "../../components/tutorials/TutorialTree";
import TutorialPlayer from "../../components/tutorials/TutorialPlayer";

let _msgId = 1000;
const nextId = () => `m${++_msgId}`;

const GREETING = {
  id: nextId(),
  role: "bot",
  content:
    "Hi! I'm Growfin AI — your platform support assistant.\n\n" +
    "I can help you with:\n" +
    "• Understanding Pyramid Hedging and other strategies\n" +
    "• Building and configuring rules\n" +
    "• Navigating the platform\n" +
    "• Troubleshooting issues\n\n" +
    "What would you like to know today?",
  ts: new Date(),
};

const TAB_CHAT      = "chat";
const TAB_TUTORIALS = "tutorials";

export default function ChatPage() {
  const { user }     = useAuth();
  const location     = useLocation();
  const isAdmin      = user?.user_type === "admin" || user?.user_type === "superadmin";

  /* ── tab state — read from location.state on first mount ── */
  const initialTab       = location.state?.tab === TAB_CHAT ? TAB_CHAT : TAB_TUTORIALS;
  const initialTutorial  = location.state?.tutorialId || null;

  const [activeTab,         setActiveTab]         = useState(initialTab);
  const [highlightTutorial, setHighlightTutorial] = useState(initialTutorial);
  const [playingTutorial,   setPlayingTutorial]   = useState(null);

  /* ── chat state ── */
  const [messages,        setMessages]        = useState([GREETING]);
  const [loading,         setLoading]         = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionHistory,  setSessionHistory]  = useState([]);
  const [sidebarTab,      setSidebarTab]      = useState("mine");
  const [sidebarOpen,     setSidebarOpen]     = useState(true);
  const sessionId = useRef(null);

  /* when tutorial tab is selected via PageHelpBubble, clear highlight after visit */
  useEffect(() => {
    if (activeTab === TAB_TUTORIALS && highlightTutorial) {
      const t = setTimeout(() => setHighlightTutorial(null), 4000);
      return () => clearTimeout(t);
    }
  }, [activeTab, highlightTutorial]);

  /* ── chat helpers ── */
  const createSession = useCallback(async () => {
    const username = user?.username || "anonymous";
    try {
      const res = await axios.post("/chat/session", { username });
      return res.data.session_id;
    } catch {
      return `chat-${Date.now()}`;
    }
  }, [user]);

  const loadSessions = useCallback(async () => {
    if (!user?.username) return;
    try {
      const res = await axios.get("/chat/sessions", { params: { username: user.username } });
      setSessionHistory(res.data || []);
    } catch {
      setSessionHistory([]);
    }
  }, [user]);

  useEffect(() => {
    createSession().then(id => {
      sessionId.current = id;
      setActiveSessionId(id);
    });
    loadSessions();
  }, [createSession, loadSessions]);

  const handleSend = useCallback(async (text) => {
    const userMsg = { id: nextId(), role: "user", content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const res = await axios.post("/chat/message", {
        session_id: sessionId.current,
        message:    text,
        username:   user?.username || "anonymous",
      });
      const data = res.data;
      setMessages(prev => [...prev, {
        id: nextId(), role: "bot", content: data.reply, ts: new Date(),
        escalated: data.escalated, confidence: data.confidence,
      }]);
      if (data.token_warning) {
        setMessages(prev => [...prev, {
          id: nextId(), role: "system",
          content: "⚠️ This conversation is nearing its message limit. Start a new chat soon.",
          ts: new Date(),
        }]);
      }
      loadSessions();
    } catch (err) {
      setMessages(prev => [...prev, {
        id: nextId(), role: "bot",
        content: err.response?.data?.reply || "⚠️ Something went wrong. Please try again.",
        ts: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [user, loadSessions]);

  const startNewSession = useCallback(async () => {
    const id = await createSession();
    sessionId.current = id;
    setActiveSessionId(id);
    setMessages([{ ...GREETING, id: nextId(), ts: new Date() }]);
    loadSessions();
  }, [createSession, loadSessions]);

  /* ── switch to tutorials tab from chat ── */
  const handleSwitchToTutorials = (tutorialId) => {
    setHighlightTutorial(tutorialId || null);
    setActiveTab(TAB_TUTORIALS);
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 60px)",
      background: "#f7f8fc",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* ── top tab bar ──────────────────────────────────────────────── */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #eee",
        padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0, height: 52,
      }}>
        {/* tab buttons */}
        <div style={{ display: "flex", gap: 0 }}>
          {[
            { key: TAB_CHAT,      label: "💬 Help Chat",        badge: null },
            { key: TAB_TUTORIALS, label: "🎬 Video Tutorials",  badge: "12" },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: "none",
                border: "none",
                borderBottom: activeTab === tab.key ? "2px solid #545cd8" : "2px solid transparent",
                padding: "0 20px",
                height: 52,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 700 : 500,
                color: activeTab === tab.key ? "#545cd8" : "#888",
                display: "flex", alignItems: "center", gap: 6,
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {tab.label}
              {tab.badge && (
                <span style={{
                  background: activeTab === tab.key ? "#545cd8" : "#ddd",
                  color: activeTab === tab.key ? "#fff" : "#888",
                  fontSize: 10, fontWeight: 700,
                  borderRadius: 20, padding: "1px 6px",
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* context hint */}
        {activeTab === TAB_TUTORIALS && highlightTutorial && (
          <div style={{
            fontSize: 12, color: "#545cd8",
            background: "#f0edff", border: "1px solid #d4ccff",
            borderRadius: 20, padding: "3px 12px",
            animation: "fadeIn 0.3s ease",
          }}>
            <style>{`@keyframes fadeIn { from { opacity:0 } to { opacity:1 } }`}</style>
            📍 Highlighted based on your current page
          </div>
        )}
      </div>

      {/* ── tab content ──────────────────────────────────────────────── */}
      {activeTab === TAB_CHAT ? (
        /* ── HELP CHAT tab ── */
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* sidebar */}
          <div style={{
            width: sidebarOpen ? 280 : 0, minWidth: sidebarOpen ? 280 : 0,
            overflow: "hidden", background: "#fff", borderRight: "1px solid #eee",
            display: "flex", flexDirection: "column",
            transition: "width 0.25s ease, min-width 0.25s ease", flexShrink: 0,
          }}>
            <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid #eee", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: "#333" }}>💬 Conversations</span>
                <button
                  onClick={startNewSession}
                  style={{
                    background: "linear-gradient(135deg, #545cd8 0%, #764ba2 100%)",
                    border: "none", borderRadius: 8, color: "#fff",
                    fontSize: 11, fontWeight: 600, padding: "5px 10px", cursor: "pointer",
                  }}
                >
                  + New
                </button>
              </div>
              {isAdmin && (
                <div style={{ display: "flex", gap: 4 }}>
                  {["mine", "all"].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setSidebarTab(tab)}
                      style={{
                        flex: 1, padding: "5px 0",
                        background: sidebarTab === tab ? "#f0edff" : "none",
                        border: `1px solid ${sidebarTab === tab ? "#545cd8" : "#e0e0e0"}`,
                        borderRadius: 6,
                        color: sidebarTab === tab ? "#545cd8" : "#888",
                        fontSize: 11, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
                      }}
                    >
                      {tab === "mine" ? "My chats" : "All users"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              {activeSessionId && (
                <SessionItem
                  session={{ id: activeSessionId, title: "Current session", ts: new Date(), isActive: true }}
                  isSelected={true} onClick={() => {}}
                />
              )}
              {sessionHistory.filter(s => s.id !== activeSessionId).length > 0 && (
                <div style={{ padding: "4px 16px 2px", fontSize: 10, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  History
                </div>
              )}
              {sessionHistory.filter(s => s.id !== activeSessionId).map(session => (
                <SessionItem
                  key={session.id}
                  session={{ ...session, ts: new Date(session.updated_at || session.created_at), messageCount: session.message_count }}
                  isSelected={false} onClick={() => {}}
                />
              ))}
              {sidebarTab === "all" && isAdmin && <AdminSessionList />}
            </div>

            {/* tutorial shortcut */}
            <div style={{
              padding: "12px 16px",
              borderTop: "1px solid #eee",
              background: "#fafafa",
            }}>
              <button
                onClick={() => handleSwitchToTutorials(null)}
                style={{
                  width: "100%", background: "#f0edff",
                  border: "1px solid #d4ccff", borderRadius: 8,
                  color: "#545cd8", fontSize: 12, fontWeight: 600,
                  padding: "8px 0", cursor: "pointer",
                }}
              >
                🎬 Browse Video Tutorials
              </button>
            </div>
          </div>

          {/* chat area */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{
              padding: "0 20px", height: 56,
              background: "#fff", borderBottom: "1px solid #eee",
              display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  onClick={() => setSidebarOpen(v => !v)}
                  style={{
                    background: "none", border: "1px solid #e0e0e0",
                    borderRadius: 6, padding: "5px 8px",
                    cursor: "pointer", color: "#666", fontSize: 14,
                  }}
                  title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
                >
                  ☰
                </button>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "linear-gradient(135deg, #545cd8 0%, #764ba2 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                }}>
                  🤖
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#333" }}>Growfin AI Support</div>
                  <div style={{ fontSize: 11, color: "#28a745" }}>● Online</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#aaa" }}>
                {activeSessionId ? `Session: ${activeSessionId.slice(0, 12)}…` : "Starting session…"}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <ChatWindow
                messages={messages}
                onSend={handleSend}
                loading={loading}
                compact={false}
                sessionId={sessionId.current}
              />
            </div>
          </div>
        </div>
      ) : (
        /* ── VIDEO TUTORIALS tab ── */
        <div style={{ flex: 1, overflowY: "auto" }}>
          <TutorialTree
            highlightId={highlightTutorial}
            onPlay={setPlayingTutorial}
          />
        </div>
      )}

      {/* ── tutorial player modal ── */}
      {playingTutorial && (
        <TutorialPlayer
          tutorial={playingTutorial}
          onClose={() => setPlayingTutorial(null)}
        />
      )}
    </div>
  );
}

/* ── session sidebar item ── */
function SessionItem({ session, isSelected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "10px 16px", cursor: "pointer",
        background: isSelected ? "#f0edff" : "transparent",
        borderLeft: isSelected ? "3px solid #545cd8" : "3px solid transparent",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => !isSelected && (e.currentTarget.style.background = "#f8f8f8")}
      onMouseLeave={e => !isSelected && (e.currentTarget.style.background = "transparent")}
    >
      <div style={{
        fontSize: 13, fontWeight: isSelected ? 600 : 400,
        color: isSelected ? "#545cd8" : "#333",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {session.isActive ? "🟢 " : ""}{session.title || "Untitled conversation"}
      </div>
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
        {formatRelativeTime(session.ts)}
        {session.messageCount ? `  ·  ${session.messageCount} messages` : ""}
      </div>
    </div>
  );
}

/* ── admin session list ── */
function AdminSessionList() {
  const MOCK_ADMIN_SESSIONS = [
    { id: "u1s1", username: "ROTC1004", title: "Can't see my positions",   ts: new Date(Date.now() - 7200000),  messageCount: 4, escalated: true },
    { id: "u2s1", username: "DEMO001",  title: "Delta hedging rules",       ts: new Date(Date.now() - 14400000), messageCount: 7, escalated: false },
  ];
  return (
    <>
      <div style={{ padding: "12px 16px 2px", fontSize: 10, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        All users
      </div>
      {MOCK_ADMIN_SESSIONS.map(s => (
        <div key={s.id} style={{ padding: "10px 16px", cursor: "pointer", borderLeft: "3px solid transparent" }}
          onMouseEnter={e => e.currentTarget.style.background = "#f8f8f8"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>{s.username}</span>
            {s.escalated && (
              <span style={{
                fontSize: 9, background: "#fff3cd", color: "#856404",
                border: "1px solid #ffc107", borderRadius: 10, padding: "1px 7px",
              }}>
                Needs reply
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#777", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s.title}
          </div>
          <div style={{ fontSize: 10, color: "#bbb", marginTop: 1 }}>{formatRelativeTime(s.ts)}</div>
        </div>
      ))}
    </>
  );
}

/* ── helpers ── */
function formatRelativeTime(ts) {
  if (!ts) return "";
  const diff  = Date.now() - (ts instanceof Date ? ts.getTime() : new Date(ts).getTime());
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}
